<template>
  <b-card no-body class="processing-card">
    <b-card-header class="processing-header">
      <div class="header-title">
        <i class="fas fa-cogs mr-2"></i>
        <strong>Processamento</strong>
      </div>
      <div class="header-actions">
        <b-button
          v-if="canEdit || canReopen"
          :disabled="loadingEvents"
          variant="primary"
          size="sm"
          @click="$emit('add-event')"
          >Adicionar Movimentação
        </b-button>
        <b-form-checkbox v-model="showAddedEvents" switch size="sm" class="ml-3"
          >Mostrar movimentações</b-form-checkbox
        >
        <b-form-checkbox v-model="showAuditEvents" switch size="sm" class="ml-2"
          >Mostrar auditoria</b-form-checkbox
        >
        <b-form-checkbox
          v-if="canHideIrrelevant && hasIrrelevantEvents"
          v-model="showIrrelevant"
          switch
          size="sm"
          class="ml-2"
          >Mostrar irrelevantes</b-form-checkbox
        >
      </div>
    </b-card-header>
    <em v-if="loadingEvents">Carregando...</em>
    <template v-else>
      <b-card
        v-for="event in filteredEvents"
        :id="`event-${event._id}`"
        :key="event._id"
        no-header
        :class="{
          'event-irrelevant': event.irrelevant && event.irrelevant.marked,
          'text-secondary': event.irrelevant && event.irrelevant.marked,
        }"
      >
        <b-button
          v-if="
            canHideIrrelevant &&
            !cannotMarkAsIrrelevant(event) &&
            !(event.irrelevant && event.irrelevant.marked)
          "
          size="sm"
          variant="outline-secondary"
          class="float-right irrelevant-toggle"
          title="Marcar como irrelevante"
          @click="$emit('toggle-irrelevant', event)"
        >
          <i class="fa fa-eye-slash" aria-hidden="true"></i>
          Marcar como irrelevante
        </b-button>
        <div>#{{ event.number }}</div>
        <label><strong>Tipo:</strong></label>
        {{ labelEventType(event.type) }}

        <template v-if="event.type === EventType.CLOSURE">
          <br />
          <label class="card-text">
            <strong>Indicador de Encerramento: </strong>
            {{ classificationMaps.complaintClosure[event.closure] }}
          </label>
        </template>

        <template v-if="event.type === EventType.TIME_COST && event.timeCost">
          <br />
          <b-row class="mb-2">
            <b-col cols="12">
              <label class="card-text">
                <strong>Tempo Gasto: </strong>
                {{ event.timeCost.timeSpent || 0 }} minutos
              </label>
            </b-col>
            <b-col
              v-if="
                complaint.company.financial &&
                complaint.company.financial.showTimeCost
              "
              cols="12"
            >
              <label class="card-text">
                <strong>Custo: </strong>
                R$ {{ event.timeCost.cost || 0 | currency }}
              </label>
            </b-col>
          </b-row>
          <p class="card-text">
            <label><strong>Descrição do Trabalho:</strong></label>
            {{ event.timeCost.description || "Não informada" }}
          </p>
        </template>

        <label
          v-if="event.type === EventType.TASK && event.task"
          class="card-text"
        >
          <strong>Destinatário: </strong>
          {{ event.task.email || "Não informado" }}
        </label>
        <br />

        <p
          v-if="
            ![
              EventType.MONITORING_RESPONSE_ENABLED,
              EventType.MONITORING_RESPONSE_DISABLED,
              EventType.COMMITTEE,
              EventType.CLASSIFICATION,
              EventType.TIME_COST,
              EventType.DENOUNCED_UNLINK,
            ].includes(event.type) &&
            !(event.irrelevant && event.irrelevant.marked)
          "
          class="card-text"
        >
          <label><strong>Descrição:</strong></label>
          {{ event.description }}
        </p>
        <p
          v-if="event.irrelevant && event.irrelevant.marked"
          class="card-text font-italic"
        >
          <i class="fa fa-eye-slash mr-1" aria-hidden="true"></i>
          <strong>Movimentação marcada como irrelevante.</strong>
          Justificativa: {{ event.irrelevant.justification }}
        </p>

        <template
          v-if="
            event.type === EventType.DENOUNCED_UNLINK && event.denouncedUnlink
          "
        >
          <b-row class="mb-2">
            <b-col cols="12">
              <label class="card-text">
                <strong>Denunciado removido:</strong>
                {{ event.denouncedUnlink.name || "Nome não informado" }}
                <span v-if="event.denouncedUnlink.cpfcnpj">
                  ({{ event.denouncedUnlink.cpfcnpj }})
                </span>
              </label>
            </b-col>
            <b-col
              cols="12"
              v-if="
                event.denouncedUnlink.office || event.denouncedUnlink.department
              "
            >
              <label class="card-text">
                <strong>Cargo/Depto:</strong>
                {{ event.denouncedUnlink.office }}
                <span v-if="event.denouncedUnlink.department">
                  - {{ event.denouncedUnlink.department }}
                </span>
              </label>
            </b-col>
            <b-col cols="12">
              <p class="card-text mb-0">
                <strong>Justificativa:</strong>
                {{ event.denouncedUnlink.justification || "Não informada" }}
              </p>
            </b-col>
          </b-row>
        </template>

        <template
          v-if="
            event.type === EventType.COURSE_ASSIGNMENT && event.courseAssignment
          "
        >
          <b-row class="mb-2">
            <b-col cols="12">
              <label class="card-text">
                <strong>Denunciado:</strong>
                {{
                  event.courseAssignment.denouncedName || "Nome não informado"
                }}
              </label>
            </b-col>
            <b-col cols="12" v-if="event.courseAssignment.denouncedEmail">
              <label class="card-text">
                <strong>E-mail:</strong>
                {{ event.courseAssignment.denouncedEmail }}
              </label>
            </b-col>
            <b-col cols="12" v-if="event.courseAssignment.denouncedCPF">
              <label class="card-text">
                <strong>CPF:</strong>
                {{ event.courseAssignment.denouncedCPF }}
              </label>
            </b-col>
            <b-col cols="12">
              <label class="card-text">
                <strong>Curso:</strong>
                {{
                  event.courseAssignment.courseName ||
                  event.courseAssignment.courseId
                }}
              </label>
            </b-col>
            <b-col cols="12">
              <label class="card-text">
                <strong>Status:</strong>
                <b-badge
                  :variant="
                    getCourseAssignmentStatusVariant(
                      event.courseAssignment.status
                    )
                  "
                  class="ml-2"
                >
                  {{
                    getCourseAssignmentStatusText(event.courseAssignment.status)
                  }}
                </b-badge>
              </label>
            </b-col>
            <b-col
              cols="12"
              v-if="
                event.courseAssignment.status === 'completed' &&
                event.courseAssignment.certificateNumber
              "
            >
              <label class="card-text">
                <strong>Certificado:</strong>
                {{ event.courseAssignment.certificateNumber }}
              </label>
            </b-col>
            <!-- Exibir informações do certificado pré-existente -->
            <b-col
              cols="12"
              v-if="
                event.courseAssignment.alreadyCertified &&
                event.courseAssignment.certificate
              "
            >
              <b-alert show variant="success" class="mt-2 mb-0">
                <i class="fa fa-check-circle"></i>
                <strong> Certificado Pré-Existente</strong>
                <div class="mt-2">
                  <strong>Número:</strong>
                  {{ event.courseAssignment.certificate.number }}
                </div>
                <div v-if="event.courseAssignment.certificate.issuedAt">
                  <strong>Emitido em:</strong>
                  {{ formatDate(event.courseAssignment.certificate.issuedAt) }}
                </div>
                <div v-if="event.courseAssignment.certificate.score">
                  <strong>Nota:</strong>
                  {{ event.courseAssignment.certificate.score }}%
                </div>
              </b-alert>
            </b-col>
            <!-- Indicador de matrícula não duplicada -->
            <b-col
              cols="12"
              v-if="
                event.courseAssignment.alreadyEnrolled &&
                !event.courseAssignment.alreadyCertified
              "
            >
              <b-alert show variant="info" class="mt-2 mb-0">
                <i class="fa fa-info-circle"></i>
                <strong> Matrícula Existente</strong>
                <div class="mt-1">
                  Denunciado já estava matriculado. Matrícula não foi duplicada.
                </div>
              </b-alert>
            </b-col>
          </b-row>
        </template>

        <template
          v-if="
            [
              EventType.WITH_ATTACHMENT,
              EventType.TASK,
              EventType.SOLVE_TASK,
            ].includes(event.type)
          "
        >
          <b-list-group v-if="event.files && event.files.length > 0">
            <b-list-group-item
              v-for="(file, idx) in event.files"
              :key="idx"
              class="d-flex justify-content-between align-items-center"
            >
              {{ file ? file.name : "Arquivo sem nome" }}
              <b-button
                v-if="file"
                variant="link"
                :href="`/api/complaints/${event.complaint}/events/${event._id}/file/${file._id}`"
                target="_blank"
              >
                <i class="fa fa-download" aria-hidden="true"></i>
                Download
              </b-button>
            </b-list-group-item>
          </b-list-group>
          <b-alert
            v-else-if="event.type === EventType.WITH_ATTACHMENT"
            show
            variant="warning"
            >Arquivo não recebido!</b-alert
          >
        </template>

        <b-row
          v-if="
            event.type === EventType.COMMITTEE &&
            event.committee &&
            event.committee.new &&
            event.committee.old
          "
          class="mb-2"
        >
          <b-col v-if="event.committee.new.selectedCommitteeName" cols="12">
            <strong>Comitê Selecionado:</strong>
            {{ event.committee.new.selectedCommitteeName }}
          </b-col>
          <b-col
            v-if="
              event.committee.old.hideDenouncerData !==
              event.committee.new.hideDenouncerData
            "
            cols="12"
          >
            <strong>Dados do denunciante:</strong>
            {{
              event.committee.new.hideDenouncerData
                ? "ocultados"
                : "desocultados"
            }}
          </b-col>
          <b-col v-if="eventCommitteeRemovedList(event.committee)" cols="12">
            <strong>Excluido:</strong>
            {{ eventCommitteeRemovedList(event.committee) }}
          </b-col>
          <b-col v-if="eventCommitteeAddedList(event.committee)" cols="12">
            <strong>Adicionado:</strong>
            {{ eventCommitteeAddedList(event.committee) }}
          </b-col>
          <b-col cols="12">
            <strong>Comissão Atual:</strong>
            {{ eventCommitteeCurrent(event.committee) }}
          </b-col>
        </b-row>

        <b-row
          v-if="event.type === EventType.CLASSIFICATION && event.classification"
          class="mb-2"
        >
          <b-col cols="12">
            <b-table
              small
              :fields="[
                { key: 'name', label: 'Classificação' },
                { key: 'old', label: 'Antes' },
                { key: 'new', label: 'Depois' },
              ]"
              :items="eventClassificationChangedList(event.classification)"
            />
          </b-col>
        </b-row>

        <b-row
          v-if="event.pendency && event.pendency.user"
          :class="{ 'text-danger': !event.pendency.resolvedBy }"
          class="mb-1 font-italic"
        >
          <b-col cols="12">
            <label>
              <strong>Pendência para: </strong>
              <template v-if="companyUsers[event.pendency.user]">
                {{ companyUsers[event.pendency.user].name }}
                {{ companyUsers[event.pendency.user].surname }}
              </template>
              <template v-else-if="managedByCompanyUsers[event.pendency.user]">
                <i class="fa fa-user-tie" aria-hidden="true"></i>
                {{ managedByCompanyUsers[event.pendency.user].name }}
                {{ managedByCompanyUsers[event.pendency.user].surname }}
              </template>
              <template v-else>
                <i class="fa fa-user-times" aria-hidden="true"></i>
                Usuário desconhecido ({{ event.pendency.user }})
              </template>
            </label>
            <br />
            <label v-if="event.pendency.dueDate">
              <strong>Prazo: </strong>{{ event.pendency.dueDate | date }}
            </label>
            <br />
            <b-button
              v-if="!event.pendency.resolvedBy && canEdit"
              variant="outline-danger"
              size="sm"
              @click="$emit('resolve-pendency', event)"
              >Resolver
            </b-button>
            <label v-else-if="event.pendency.resolvedBy"
              >Resolvida pela movimentação
              <b-button
                variant="link"
                class="p-0 pb-1"
                @click="showEvent(event.pendency.resolvedBy)"
              >
                #{{ getEventNumber(event.pendency.resolvedBy) }}
              </b-button>
            </label>
          </b-col>
        </b-row>

        <b-row
          v-if="event.pendency && event.pendency.resolves"
          class="mb-1 font-italic"
        >
          <b-col cols="12">
            <label
              >Resolve a pendência da movimentação
              <b-button
                variant="link"
                class="p-0 pb-1 font-italic"
                @click="showEvent(event.pendency.resolves)"
              >
                #{{ getEventNumber(event.pendency.resolves) }}
              </b-button>
            </label>
          </b-col>
        </b-row>

        <b-row
          v-if="event.type === EventType.TASK"
          :class="{ 'text-danger': !event.task.resolvedBy }"
          class="mb-1 font-italic"
        >
          <b-col cols="12">
            <br />
            <label v-if="event.task.dueDate">
              <strong>Prazo: </strong>{{ event.task.dueDate | date }}
            </label>
            <br />
            <b-button
              v-if="!event.task.resolvedBy && canEdit"
              variant="outline-danger"
              size="sm"
              @click="$emit('resolve-task', event)"
              >Resolver
            </b-button>
            <b-button
              v-if="!event.task.resolvedBy && canEdit"
              size="sm"
              variant="outline-warning"
              class="ml-3"
              @click="$emit('resend-task', event)"
            >
              Reenviar e-mail.
            </b-button>
            <label v-if="event.task.resolvedBy"
              >Resolvida pela movimentação
              <b-button
                variant="link"
                class="p-0 pb-1"
                @click="showEvent(event.task.resolvedBy)"
              >
                #{{ getEventNumber(event.task.resolvedBy) }}
              </b-button>
            </label>
          </b-col>
        </b-row>

        <b-row
          v-if="event.task && event.task.resolves"
          class="mb-1 font-italic"
        >
          <b-col cols="12">
            <label
              >Resolve a tarefa de terceiros da movimentação
              <b-button
                variant="link"
                class="p-0 pb-1 font-italic"
                @click="showEvent(event.task.resolves)"
              >
                #{{ getEventNumber(event.task.resolves) }}
              </b-button>
            </label>
          </b-col>
        </b-row>

        <p class="card-text">
          <small class="text-muted">
            <i class="fa fa-calendar-alt" aria-hidden="true"></i>
            {{ event.createdAt | datetime }}
            <template v-if="event.user">
              - <i class="fa fa-user" aria-hidden="true"></i>
              {{ event.user.name }} {{ event.user.surname }}
            </template>
            <template v-if="EventType.SOLVE_TASK === event.type">
              -
              <i class="fa fa-user-friends text-warning" aria-hidden="true"></i>
              <em> enviado para {{ event.task.email }}</em>
              <em v-if="event.task.resolvedBy">
                e resolvida por #{{ getEventNumber(event.task.resolvedBy) }}</em
              >
            </template>
          </small>
        </p>
      </b-card>
      <div
        v-if="!loadingEvents && filteredEvents.length === 0"
        class="text-muted small p-3"
      >
        Nenhuma movimentação para exibir com os filtros atuais.
      </div>
    </template>
  </b-card>
</template>

<script>
import { labelEventType } from "../utils";
import EventType from "../../../../../backend/models/EventType.ts";
import { ClassificationName } from "../../../../../backend/models/ClassificationType.ts";

export default {
  name: "ProcessingCard",
  props: {
    loadingEvents: { type: Boolean, default: false },
    events: { type: Array, default: () => [] },
    canEdit: { type: Boolean, default: false },
    canReopen: { type: Boolean, default: false },
    canHideIrrelevant: { type: Boolean, default: false },
    classificationMaps: { type: Object, required: true },
    companyUsers: { type: Object, default: () => ({}) },
    managedByCompanyUsers: { type: Object, default: () => ({}) },
    getCommitteeUser: { type: Function, required: true },
    complaint: { type: Object, required: true },
  },
  data() {
    return {
      EventType,
      labelEventType,
      ClassificationName,
      showAddedEvents: true,
      showAuditEvents: true,
      showIrrelevant: false,
    };
  },
  computed: {
    filteredEvents() {
      return this.events.filter((e) => {
        const isIrrelevant = !!(e && e.irrelevant && e.irrelevant.marked);
        if (isIrrelevant && !this.canHideIrrelevant) return false;
        if (isIrrelevant && this.canHideIrrelevant && !this.showIrrelevant) {
          return false;
        }
        const isAudit = this.isAuditEvent(e);
        if (!this.showAuditEvents && isAudit) return false;
        if (!this.showAddedEvents && !isAudit) return false;
        return true;
      });
    },
    hasIrrelevantEvents() {
      return this.events.some((e) => e && e.irrelevant && e.irrelevant.marked);
    },
  },
  methods: {
    getCourseAssignmentStatusVariant(status) {
      const variants = {
        pending: "warning",
        in_progress: "info",
        completed: "success",
        cancelled: "danger",
      };
      return variants[status] || "secondary";
    },
    getCourseAssignmentStatusText(status) {
      const texts = {
        pending: "Pendente",
        in_progress: "Em Progresso",
        completed: "Concluído",
        cancelled: "Cancelado",
      };
      return texts[status] || status;
    },
    formatDate(dateString) {
      if (!dateString) return "Data não disponível";
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    isAuditEvent(event) {
      const auditTypes = [
        EventType.COMMITTEE,
        EventType.CLASSIFICATION,
        EventType.DENOUNCED_UNLINK,
        EventType.MONITORING_RESPONSE_ENABLED,
        EventType.MONITORING_RESPONSE_DISABLED,
      ];
      return auditTypes.includes(event.type);
    },
    cannotMarkAsIrrelevant(event) {
      // Auditoria + tarefas internas (PENDENCY) e de terceiros (TASK).
      // Tarefas precisam ser baixadas via resolução, não escondidas.
      if (this.isAuditEvent(event)) return true;
      return event.type === EventType.PENDENCY || event.type === EventType.TASK;
    },
    showEvent(id) {
      const element = document.getElementById(`event-${id}`);
      const offset = 55;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    },
    getEventNumber(id) {
      const found = this.events.find((e) => e._id === id);
      return found ? found.number : id;
    },
    eventCommitteeAddedList(committee) {
      if (!committee || !committee.new || !committee.old) return "";
      if (!committee.new.users || !committee.old.users) return "";

      const result = committee.new.users
        .filter((user) => !committee.old.users.includes(user))
        .map((user) => {
          const committeeUser = this.getCommitteeUser(user);
          if (!committeeUser) return `Usuário desconhecido (${user})`;
          return `${committeeUser.name} ${committeeUser.surname} (comitê)`;
        });
      if (committee.new.admin && committee.new.admin !== committee.old.admin) {
        const user = committee.new.admin;
        const committeeUser = this.getCommitteeUser(user);
        if (committeeUser) {
          result.push(
            `${committeeUser.name} ${committeeUser.surname} (relator)`
          );
        } else {
          result.push(`Usuário desconhecido (${user}) (relator)`);
        }
      }
      return result.join(", ");
    },
    eventCommitteeRemovedList(committee) {
      if (!committee || !committee.new || !committee.old) return "";
      if (!committee.new.users || !committee.old.users) return "";

      const result = committee.old.users
        .filter((user) => !committee.new.users.includes(user))
        .map((user) => {
          const committeeUser = this.getCommitteeUser(user);
          if (!committeeUser) return `Usuário desconhecido (${user})`;
          return `${committeeUser.name} ${committeeUser.surname} (comitê)`;
        });
      if (committee.old.admin && committee.old.admin !== committee.new.admin) {
        const user = committee.old.admin;
        const committeeUser = this.getCommitteeUser(user);
        if (committeeUser) {
          result.push(
            `${committeeUser.name} ${committeeUser.surname} (relator)`
          );
        } else {
          result.push(`Usuário desconhecido (${user}) (relator)`);
        }
      }
      return result.join(", ");
    },
    eventCommitteeCurrent(committee) {
      if (!committee || !committee.new) return "Sem comissão";
      if (!committee.new.users) return "Sem comissão";

      const result = committee.new.users.map((user) => {
        const committeeUser = this.getCommitteeUser(user);
        if (!committeeUser) return `Usuário desconhecido (${user})`;
        return `${committeeUser.name} ${committeeUser.surname} (comitê)`;
      });
      if (committee.new.admin) {
        const user = committee.new.admin;
        const committeeUser = this.getCommitteeUser(user);
        if (committeeUser) {
          result.push(
            `${committeeUser.name} ${committeeUser.surname} (relator)`
          );
        } else {
          result.push(`Usuário desconhecido (${user}) (relator)`);
        }
      }
      if (result.length === 0) return "Sem comissão";
      return result.join(", ");
    },
    eventClassificationChangedList(data) {
      if (!data || !data.new || !data.old) return [];

      const result = Object.entries(data.new)
        .filter(([key, value]) => data.old[key] !== value)
        .map(([key, value]) => {
          return {
            name: this.ClassificationName[key] || key,
            new: this.classificationMaps[key]
              ? this.classificationMaps[key][value]
              : value,
            old: this.classificationMaps[key]
              ? this.classificationMaps[key][data.old[key]]
              : data.old[key],
          };
        });
      return result;
    },
  },
};
</script>

<style scoped>
.event-irrelevant {
  background-color: #f8f9fa;
  border-left: 3px solid #adb5bd;
}

.irrelevant-toggle {
  margin-bottom: 0.5rem;
}
</style>
